import { Button, Form, Spinner } from 'react-bootstrap';
import { ConsentContext, ConsentFlows } from '../../context/ConsentContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import { error, primary } from '../../theme/colors';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';
import {
  registerMpanIhdInternal,
  registrationMethods,
  validationCodes,
} from '../../api/SmartMeter';

import { Auth } from 'aws-amplify';
import { CallMade } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PaymentCard from '../../ui/Icon/PaymentCard';
import ReactMarkdown from 'react-markdown';
import { SpacerHr } from '../../ui/Styled';
import VerificationInput from 'react-verification-input';
import { VerifyMethodBlock } from '../../ui';
import cx from 'classnames';
import errorIcon from '../../assets/svg/error-icon.svg';
import ihdGuidExample from '../../assets/svg/ihd-guid-example.svg';
import ihdGuidExampleMobile from '../../assets/svg/ihd-guid-example-mobile.svg';
import infoOutline from '../../assets/svg/info-outline.svg';
import moment from 'moment';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledH1 = styled.h1`
  margin-bottom: 16px;
`;

const WrapperDiv = styled.div`
  display: flex;
`;

const StyledImg = styled.img`
  margin-right: 10px;
  width: 20px;
`;

const StyledReactMarkdown = styled(ReactMarkdown)`
  margin-top: 6px;
  hr {
    margin-top: 30px;
    margin-bottom: 30px;
  }
  p:last-child {
    text-align: left;
    margin-top: 10px;
  }
`;

const StyledReactMarkdownExta = styled(StyledReactMarkdown)`
  em {
    font-style: normal;
    font-weight: bold;
    color: ${primary};
  }
`;

const StyledDiv = styled.div`
  text-align: left;
`;

const StyledWrapperDiv = styled(StyledDiv)`
  border: 1px solid #afb6b9;
  border-radius: 4px;
  position: relative;
  padding: 15px 15px;
  margin-bottom: 10px;

  p {
    margin-bottom: 0;
  }
`;

const StyledCallMade = styled(CallMade)`
  font-weight: 800;
  color: ${primary};
  font-size: 1rem !important;
`;

const VerificationWrapper = styled.div`
  &.error {
    .verification-input__character {
      border-color: ${error} !important;
      background-color: ${error}22 !important;
    }
  }
`;

const ErrorDiv = styled.div`
  color: ${error};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  font-size: 12px;
  margin-top: 90px;

  a {
    color: ${error};
  }
`;

const MeterCodeContainer = ({ loading, fetched, contentful }) => {
  const navigate = useNavigate();
  const {
    mpan,
    setMethod,
    consentFlow,
    moveDate,
    termsAndConditions,
    lookedUpMpanHash,
    verificationMethods,
  } = useContext(ConsentContext);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [ihdMacSuffix, setIhdMacSuffix] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isValid, setIsValid] = useState(false);
  const codeInputRef = useRef(null);

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.YOUR_SMART_METER_GUID_MAC_EUI);
    };
    asyncRecordFlowProgress();
  }, []);

  const onChange = value => {
    setIhdMacSuffix(value);
    if (validateMac(value)) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const onSubmit = value => {
    setErrorMessage(undefined);
    setMethod(registrationMethods.IHD);
    Auth.currentAuthenticatedUser().then(async user => {
      const moveInDateMonth = moveDate ? moment(moveDate).format('MM') : '';
      const moveInDateYear = moveDate ? moment(moveDate).format('YYYY') : '';
      const addressId = user.attributes.address;
      const mpanAutoFilled = lookedUpMpanHash === mpan;
      const ucIhdMacSuffix = ihdMacSuffix.toUpperCase();

      // show the spinner
      setIsRegistering(true);

      // send values
      const res = await registerMpanIhdInternal(
        mpan,
        moveInDateMonth,
        moveInDateYear,
        termsAndConditions,
        addressId,
        mpanAutoFilled,
        ucIhdMacSuffix,
        verificationMethods.ihd.details.readInventoryId,
        consentFlow === ConsentFlows.RECONFIRM,
      );

      // hide the spinner
      setIsRegistering(false);

      if (
        res.status !== 200 &&
        res.code !== validationCodes.ALREADY_REGISTERED
      ) {
        setErrorMessage(res.message);
        recordFlowProgress(flowSteps.CANNOT_CONFIRM_IHD);
      } else {
        navigate(`/${consentFlow}/success`);
      }
    });
  };

  const onTryWithPaymentCard = async e => {
    e.preventDefault();
    await recordFlowProgress(flowSteps.DEFAULT_IHD_W_LINK_BACK_TO_PAYMENT_CARD);
    navigate(`/${consentFlow}/card-details`);
  };

  const validateMac = value => {
    const ucValue = `${value}`.toUpperCase();
    return ucValue.match(/[A-F0-9]{4}/gm);
  };

  return (
    !loading &&
    fetched && (
      <Form
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}>
        <p>
          Step <strong>3</strong> of <strong>3</strong>
        </p>
        <StyledH1>{contentful?.title}</StyledH1>
        <StyledReactMarkdown>{contentful?.body}</StyledReactMarkdown>
        <WrapperDiv>
          <StyledImg src={infoOutline} alt="info" />
          <StyledReactMarkdownExta>
            {contentful?.bodyExtra.replace(
              '{user-mac}',
              verificationMethods?.ihd?.details?.prefix,
            )}
          </StyledReactMarkdownExta>
        </WrapperDiv>
        <VerificationWrapper
          className={cx({
            'meter-code-wrapper': true,
            error: !!errorMessage,
          })}>
          <VerificationInput
            ref={codeInputRef}
            length={4}
            validChars="a-fA-F0-9"
            placeholder="-"
            onChange={onChange}
            classNames={{
              container: 'verification-input-container',
              character: 'verification-input-character',
              characterInactive: 'verification-input-character--inactive',
              characterSelected: 'verification-input-character--selected',
            }}
          />
          {errorMessage && (
            <ErrorDiv>
              <StyledImg src={errorIcon} alt="info" />
              <div>
                Something went wrong, please check the last 4 digits of the
                GUID/MAC/EUI code from your IHD and re-enter them, or{' '}
                <a
                  onClick={onTryWithPaymentCard}
                  href={`/${consentFlow}/card-details`}>
                  try with a payment card
                </a>{' '}
                (you won’t be charged).
              </div>
            </ErrorDiv>
          )}
        </VerificationWrapper>
        <StyledDiv>{contentful?.subtitle}</StyledDiv>
        <StyledDiv>
          {showExamples || (
            <div>
              <Button
                className="pl-0 py-0"
                variant="link"
                onClick={() => setShowExamples(true)}>
                Show examples
                <FontAwesomeIcon
                  className="em-text pt-1 pl-2"
                  icon={faChevronDown}
                />
              </Button>{' '}
            </div>
          )}
        </StyledDiv>
        {showExamples && (
          <>
            <div className="text-left mb-3">
              <Button
                className="pl-0 pt-0"
                variant="link"
                onClick={() => setShowExamples(false)}>
                Hide examples
                <FontAwesomeIcon
                  className="em-text pt-1 pl-2"
                  icon={faChevronUp}
                />
              </Button>{' '}
            </div>
            <div className="py-2">
              <img
                alt="IHD GUID Example"
                src={ihdGuidExample}
                className="w-100 d-none d-sm-block"
              />
              <img
                alt="IHD GUID Example"
                src={ihdGuidExampleMobile}
                className="w-100 d-block d-sm-none"
              />
            </div>
            <StyledWrapperDiv>
              <ReactMarkdown>{contentful?.bodyEnd}</ReactMarkdown>
              <a href={contentful?.link}>
                Show me the video <StyledCallMade />
              </a>
            </StyledWrapperDiv>
          </>
        )}
        {!isRegistering ? (
          <Button
            className="mt-3 mb-3 w-100"
            variant="primary"
            type="submit"
            disabled={!isValid}
            onClick={onSubmit}>
            {contentful?.primaryButtonText}
          </Button>
        ) : (
          <Button
            className="mt-3 mb-3 w-100"
            variant="primary"
            disabled={true}
            onClick={() => {}}>
            <Spinner animation="border" size="sm" />
          </Button>
        )}
        <SpacerHr label="or" />
        <VerifyMethodBlock
          featureLabel="Recommended"
          icon={PaymentCard}
          markdown={contentful?.bodyEndExtra}
          onClick={onTryWithPaymentCard}
          buttonLabel={contentful?.secondaryButtonText}
          buttonVariant="secondary"
        />
      </Form>
    )
  );
};

MeterCodeContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(
  MeterCodeContainer,
  'web_app_onboarding_meterinput_page',
);
